'use client';

import {
  useMemo, useEffect, useRef, useCallback, useState,
} from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'next-i18next';
import { buildObjectFromSearchParams, buildSearchParams } from '@utils/data-fetching/searchParams';
import { TalentSearchFilters } from '@components/features/filters/TalentSearchFilters';
import { useApplicantsBulkOperations } from '@components/templates/employer/applicant-list/bulk-operations/hooks/useApplicantsBulkOperations';
import { useSwrJobBasicInfo } from '@hooks/useSwrApi/endpoints/talent-search';
import { useBaseFiltersQueryParamsState } from '@hooks/filters/useBaseFiltersQueryParamsState';
import { ApplicantFilterDrawerProvider } from '@components/contexts/ApplicantFilterDrawerContext';
import { useCompanyApplicant } from '@components/contexts/CompanyApplicantContext';
import { useMcHistory } from '@hooks/history/useMcHistory';
import { useBjModal } from '@hooks/common/useBjModal';
import { getI18nLinkHref } from '@services/i18nPaths';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import {
  Button, Tooltip,
} from '@components/common';
import { Input } from '@components/common/Forms';
import { ArrowUpTrayIcon, ShareIcon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import { SquaresPlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { CompanyFloatingNavBar } from '@components/features/job-list-content/CompanyFloatingNavBar';
import { McCvUploadModal } from '@components/templates/employer/applicant-list/flow-mc/McCvUploadModal';
import { ApplicantStatus } from '@components/templates/employer/applicant-list/functions';
import {
  BulkCopyApplicants,
  BulkMessage,
  BulkSendCv,
  BulkValidate,
} from '@components/templates/employer/applicant-list/bulk-operations';
import { useSwrMcUnifiedProfileList } from '@hooks/useSwrApi/endpoints/profile';
import { extractMcUnifiedProfileQueryParams } from '@components/templates/employer/applicant-list/flow-mc/query';
import { getMcFlowStatusContext } from '@components/templates/employer/applicant-list/flow-mc/constants';
import {
  mapUnifiedProfileResultToMcApplicantsPage,
  normalizeMcBulkActionSlugs,
} from '@components/templates/employer/applicant-list/flow-mc/mappers/mapUnifiedProfileToMcApplicant';
import { McApplicantList } from '@components/templates/employer/applicant-list/flow-mc/partials/McApplicantList';
import { McUnifiedProfileQueryParams, McUnifiedProfileResult } from '@components/templates/employer/applicant-list/flow-mc/types';

const FORM_GROUP_NAME = 'applicants';

interface ApplicantListContentProps {
  locale: Locale;
  isAuthenticated?: boolean;
  initialMcUnifiedProfileQueryParams?: McUnifiedProfileQueryParams;
  serverSideUnifiedProfiles?: McUnifiedProfileResult;
}

/**
 * @description Main content panel for the company Mission Control view.
 *
 * - Shows a placeholder prompt when no job is selected and no initialSlug is in context.
 * - When a job is selected (by click or via URL), renders the applicant list with
 *   TalentSearchFilters (drawer only), an MC-specific applicants list, and the MC action bar.
 * - Filter state is local (no URL pollution); URL is managed here via useMcHistory
 *   so bookmarks work: /mc/job/[slug]/applicants?status=XX
 */
const ApplicantListContentInner = ({
  locale,
  isAuthenticated = false,
  initialMcUnifiedProfileQueryParams,
  serverSideUnifiedProfiles,
}: ApplicantListContentProps) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { push, replace, getCurrentPathname } = useMcHistory(locale);
  const { isOpen: isOpenShareModal, toggleModal: toggleShareModal } = useBjModal();
  const {
    isOpen: isUploadModalOpen,
    openModal: openUploadModal,
    closeModal: closeUploadModal,
  } = useBjModal();
  const [showBulkActions, setShowBulkActions] = useState(false);
  const {
    selectedJob, initialSlug, initialStatus, setInitialStatus, setSelectedJob,
  } = useCompanyApplicant();

  const jobSlug = selectedJob?.slug ?? initialSlug ?? '';
  const mcApplicantsPath = jobSlug ? getI18nLinkHref(`/mc/job/${jobSlug}/applicants`, locale) : '';

  const mcFlowPath = jobSlug ? getI18nLinkHref(`/mc/job/${jobSlug}/applicants/flow`, locale) : '';
  const standardFlowPath = jobSlug ? getI18nLinkHref(`/job/${jobSlug}/applicants/flow`, locale) : '';

  const {
    queryParams,
    setQueryParams,
    setCurrentPage,
  } = useBaseFiltersQueryParamsState({
    paginationType: 'page',
    keepAsCommaSeparatedValues: ['spokenLanguages'],
    serverQueryParams: initialMcUnifiedProfileQueryParams
      ?? (initialStatus !== null ? { status: initialStatus } : {}),
  });

  useEffect(() => {
    if (!selectedJob?.slug) return;
    if (initialStatus === null) return;

    const nextQueryParams: Record<string, unknown> = { status: initialStatus, withPlaceholders: true };
    const nextApplicantsPath = getI18nLinkHref(`/mc/job/${selectedJob.slug}/applicants`, locale);
    const nextUrl = `${nextApplicantsPath}${buildSearchParams(nextQueryParams, '?', false)}`;

    setQueryParams(nextQueryParams);
    push(nextUrl);
    setInitialStatus(null);
  }, [initialStatus, locale, push, selectedJob?.slug, setInitialStatus, setQueryParams]);

  useEffect(() => {
    const handlePopState = () => {
      const { search } = window.location;
      const normalizedPathname = getCurrentPathname();

      if (standardFlowPath && normalizedPathname === standardFlowPath) {
        router.replace(`${mcFlowPath}${search}`);
        return;
      }

      const isMcJobPath = mcApplicantsPath
        ? normalizedPathname === mcApplicantsPath || normalizedPathname.startsWith(`${mcApplicantsPath}/`)
        : false;

      if (!isMcJobPath) {
        setSelectedJob(null);
        setQueryParams({});
        return;
      }

      const nextQueryParams = buildObjectFromSearchParams(search) ?? {};
      setQueryParams(nextQueryParams);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [getCurrentPathname, mcApplicantsPath, mcFlowPath, router, setQueryParams, setSelectedJob, standardFlowPath]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!jobSlug) return;
    const qs = buildSearchParams(queryParams, '?', false);
    replace(`${mcApplicantsPath}${qs}`);
  }, [jobSlug, mcApplicantsPath, queryParams, replace]);


  const mcUnifiedProfileQueryParams = useMemo(
    () => extractMcUnifiedProfileQueryParams(queryParams as Record<string, unknown> | undefined),
    [queryParams],
  );
  const statusContext = useMemo(
    () => getMcFlowStatusContext(mcUnifiedProfileQueryParams),
    [mcUnifiedProfileQueryParams],
  );

  const {
    data,
    size,
    setSize,
    isLoading,
    isValidating,
    sizeAsRef,
    isEmpty,
    showLoadMore,
    mutate: revalidateApplicantList,
  } = useSwrMcUnifiedProfileList({
    locale,
    jobSlug,
    serverSideData: serverSideUnifiedProfiles,
    queryParams: mcUnifiedProfileQueryParams,
    shouldFetch: !!jobSlug,
  });

  const { data: jobBasicInfo } = useSwrJobBasicInfo(jobSlug, locale);

  const mappedData = useMemo(() => (
    data
      ?.map(mapUnifiedProfileResultToMcApplicantsPage)
      .filter(
        (
          page,
        ): page is NonNullable<ReturnType<typeof mapUnifiedProfileResultToMcApplicantsPage>> => Boolean(page),
      )
    ?? []
  ), [data]);

  const applicantList = useMemo(
    () => mappedData.flatMap((page) => page.items ?? []),
    [mappedData],
  );

  const {
    record,
    handleStep,
    formRef,
    selectAll,
    setSelectAll,
    getSelectedApplicants,
  } = useApplicantsBulkOperations({
    queryParams: queryParams ?? {},
    formGroupName: FORM_GROUP_NAME,
    list: applicantList,
  });

  const jobTitle = jobBasicInfo?.title ?? jobSlug;
  const normalizeSelectedApplicantsForBulkAction = useCallback(
    (selectedApplicants: string[]) => normalizeMcBulkActionSlugs(selectedApplicants, applicantList),
    [applicantList],
  );

  const getLockedApplicantSlugs = useCallback(() => {
    const selectedSlugs = getSelectedApplicants();
    if (!selectedSlugs.length) return [];

    const selectedSlugSet = new Set(selectedSlugs);
    return applicantList
      .filter((item) => selectedSlugSet.has(item.slug ?? '') && item.locked)
      .map((item) => item.mcBulkActionSlug ?? '')
      .filter((slug): slug is string => Boolean(slug));
  }, [applicantList, getSelectedApplicants]);

  if (!jobSlug) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-surface-100 px-6 py-16 text-center">
        <p className="text-lg font-medium text-ink">{t('mc.company.select.job')}</p>
        <p className="mt-2 text-sm text-ink-light">{t('mc.company.select.job.description')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-surface-100">
      <TalentSearchFilters
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        jobSlug={jobSlug}
        locale={locale}
        jobBasicInfo={jobBasicInfo}
        hideTopBar
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1632px] px-6 pb-28 pt-4">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <h1 className="line-clamp-1 text-[32px] font-semibold leading-none tracking-tight text-ink">
                {jobTitle}
              </h1>

              {jobSlug && jobBasicInfo?.title && (
                <>
                  <Tooltip
                    tooltipText={t('job.detail.share.modal.title')}
                    position="top"
                    colorScheme="black"
                    floating
                    useFloatingPortal
                  >
                    <Button
                      color="light"
                      className="!p-2"
                      size="sm"
                      rounding="full"
                      onClick={toggleShareModal}
                    >
                      <ShareIcon className="size-5" />
                    </Button>
                  </Tooltip>

                  <Tooltip
                    tooltipText={t('pipeline.view.button.column.view.title')}
                    position="top"
                    colorScheme="black"
                    floating
                    useFloatingPortal
                  >
                    <Button
                      color="light"
                      size="sm"
                      rounding="full"
                      className="!p-2"
                      aria-label={t('pipeline.view.button.column.view.title')}
                      title={t('pipeline.view.button.column.view.title')}
                      onClick={() => router.back()}
                    >
                      <ViewColumnsIcon className="size-5" />
                    </Button>
                  </Tooltip>

                  {statusContext === ApplicantStatus.NEW && (
                    <Tooltip
                      tooltipText={t('my-jobs.card.actions.upload-cvs')}
                      position="top"
                      colorScheme="black"
                      floating
                      useFloatingPortal
                    >
                      <button
                        type="button"
                        aria-label={t('my-jobs.card.actions.upload-cvs')}
                        onClick={openUploadModal}
                        className="flex size-8 items-center justify-center rounded-full border border-secondary/40 bg-surface text-secondary shadow-sm transition hover:border-secondary hover:text-secondary-dark"
                      >
                        <ArrowUpTrayIcon className="size-4.5" />
                      </button>
                    </Tooltip>
                  )}

                  {!showBulkActions ? (
                    <Tooltip
                      tooltipText={t('global.label.actions')}
                      position="top"
                      colorScheme="black"
                      floating
                      useFloatingPortal
                    >
                      <Button
                        color="light"
                        className="!h-11 !p-2"
                        size="sm"
                        rounding="full"
                        onClick={() => setShowBulkActions(true)}
                        aria-expanded={false}
                        aria-label={t('global.label.actions')}
                      >
                        <SquaresPlusIcon className="size-5" />
                      </Button>
                    </Tooltip>
                  ) : (
                    <div className="flex h-11 items-center gap-1 divide-x rounded-full bg-surface px-5 text-sm shadow-md">
                      <Button
                        styling="none"
                        className="mr-4 flex size-5 items-center justify-center rounded-full bg-surface-100"
                        onClick={() => setShowBulkActions(false)}
                        aria-label={t('global.btn.close')}
                        title={t('global.btn.close')}
                      >
                        <XMarkIcon className="size-4" />
                      </Button>

                      <BulkMessage
                        locale={locale}
                        jobSlug={jobSlug}
                        selectAll={selectAll}
                        getSelectedApplicants={getSelectedApplicants}
                        queryParams={queryParams}
                        normalizeSelectedApplicantsForBulkAction={normalizeSelectedApplicantsForBulkAction}
                      />

                      <BulkSendCv
                        locale={locale}
                        jobSlug={jobSlug}
                        selectAll={selectAll}
                        getSelectedApplicants={getSelectedApplicants}
                        queryParams={queryParams}
                        normalizeSelectedApplicantsForBulkAction={normalizeSelectedApplicantsForBulkAction}
                      />

                      <BulkCopyApplicants
                        locale={locale}
                        jobSlug={jobSlug}
                        selectAll={selectAll}
                        getSelectedApplicants={getSelectedApplicants}
                        queryParams={queryParams}
                        normalizeSelectedApplicantsForBulkAction={normalizeSelectedApplicantsForBulkAction}
                      />

                      <BulkValidate
                        locale={locale}
                        jobSlug={jobSlug}
                        selectAll={selectAll}
                        getSelectedApplicants={getSelectedApplicants}
                        queryParams={queryParams}
                        getLockedApplicantIds={getLockedApplicantSlugs}
                        normalizeSelectedApplicantsForBulkAction={normalizeSelectedApplicantsForBulkAction}
                      />

                      <label
                        htmlFor="selectAll"
                        title={t('global.action.toggle_select')}
                        aria-label={t('global.action.toggle_select')}
                        className="flex cursor-copy items-center pl-4"
                      >
                        <Input
                          name="selectAll"
                          type="checkbox"
                          className="cursor-copy text-secondary checked:bg-secondary"
                          checked={selectAll || false}
                          onClick={() => setSelectAll(!selectAll)}
                        />
                      </label>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <form noValidate onSubmit={handleStep} ref={formRef}>
            <McApplicantList
              data={mappedData}
              size={size}
              setSize={setSize}
              isLoading={isLoading}
              isValidating={isValidating}
              sizeAsRef={sizeAsRef}
              showLoadMore={showLoadMore}
              isEmpty={isEmpty}
              jobSlug={jobSlug}
              jobInfo={jobBasicInfo as ApiSchema<'ListReadJob'>}
              t={t}
              setCurrentPage={setCurrentPage}
              applicant={{
                record,
                name: FORM_GROUP_NAME,
                setSelectAll,
                getSelectedApplicants,
              }}
              locale={locale}
              statusContext={statusContext}
            />
          </form>
        </div>
      </div>

      <CompanyFloatingNavBar
        locale={locale}
        isAuthenticated={isAuthenticated}
      />

      {jobSlug && jobBasicInfo?.title && (
        <ShareModal
          isOpen={isOpenShareModal}
          onClose={toggleShareModal}
          ogTitle={jobBasicInfo.title}
          ogDescription={jobBasicInfo.title}
          pathnameShareUrl={getI18nLinkHref(`/job/${jobSlug}`, locale)}
        />
      )}

      <McCvUploadModal
        isOpen={isUploadModalOpen}
        onClose={closeUploadModal}
        jobSlug={jobSlug}
        onApplicantsColumnRevalidate={revalidateApplicantList}
      />
    </div>
  );
};

export const ApplicantListContent = ({
  locale,
  isAuthenticated,
  initialMcUnifiedProfileQueryParams,
  serverSideUnifiedProfiles,
}: ApplicantListContentProps) => (
  <ApplicantFilterDrawerProvider>
    <ApplicantListContentInner
      locale={locale}
      isAuthenticated={isAuthenticated}
      initialMcUnifiedProfileQueryParams={initialMcUnifiedProfileQueryParams}
      serverSideUnifiedProfiles={serverSideUnifiedProfiles}
    />
  </ApplicantFilterDrawerProvider>
);
